"use client";

import { toast } from "sonner";
import { Loader, Search, Eye, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { Card, CardTitle, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import Image from "next/image";
import { getMediaUrl } from "@/lib/media-url";
import { formatRupiah, getCategoryColorClass } from "@/lib/utils";
import { useRouter } from "next/navigation";

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const router = useRouter();

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/templates?limit=8");
      if (!res.ok) {
        throw new Error("Failed to fetch templates");
      }
      const data = await res.json();
      setTemplates(data.templates || []);
      setFilteredTemplates(data.templates || []);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An error occurred";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Filter and search logic
  useEffect(() => {
    let filtered = [...templates];

    if (searchTerm) {
      filtered = filtered.filter(
        (template) =>
          template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          template.description.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    switch (sortBy) {
      case "price-low":
        filtered.sort((a, b) => a.price - b.price);
        break;
      case "price-high":
        filtered.sort((a, b) => b.price - a.price);
        break;
      case "newest":
        filtered.sort(
          (a, b) =>
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
        );
        break;
    }

    setFilteredTemplates(filtered);
  }, [templates, searchTerm, sortBy]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  if (loading) {
    return (
      <div className="w-full min-h-[400px] flex flex-col justify-center items-center">
        <Loader className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading templates...</p>
      </div>
    );
  }

  if (error && templates.length === 0) {
    return (
      <div className="w-full min-h-[400px] flex flex-col justify-center items-center">
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-2">
            Failed to load templates
          </h3>
          <p className="text-muted-foreground mb-4">Please try again later</p>
          <Button onClick={fetchTemplates} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="text-center space-y-4">
        <h1 className="text-2xl sm:text-3xl sm:text-4xl font-bold tracking-tight">
          Koleksi Template Terbaik
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
          Discover high-quality, responsive templates to accelerate your
          development process
        </p>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mx-auto max-w-[1400px]">
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price-low">Price: Low to High</SelectItem>
              <SelectItem value="price-high">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="text-sm text-muted-foreground">
          {filteredTemplates.length} template
          {filteredTemplates.length !== 1 ? "s" : ""} found
        </div>
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="text-lg font-semibold mb-2">No templates found</h3>
          <p className="text-muted-foreground">
            Try adjusting your search or filter criteria
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 max-w-[1400px] mx-auto">
          {filteredTemplates.map((template) => (
            <Card
              key={template._id}
              className="group hover:shadow-lg transition-all duration-300 overflow-hidden hover:-translate-y-1 py-0 gap-0"
              onClick={() => router.push(`/templates/${template.slug}`)}
            >
              <CardHeader className="p-0">
                <div className="relative w-full aspect-[5/6] overflow-hidden">
                  {template.media && template.media.length > 0 ? (
                    template.media[0].type === "image" ? (
                      <Image
                        src={getMediaUrl(template.media[0].url)}
                        alt={template.title}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                        sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                      />
                    ) : (
                      <video
                        src={getMediaUrl(template.media[0].url)}
                        className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                        autoPlay
                        muted
                        loop
                        playsInline
                      />
                    )
                  ) : (
                    <div className="flex items-center justify-center h-full w-full bg-muted">
                      <p className="text-sm text-muted-foreground">
                        No preview available
                      </p>
                    </div>
                  )}

                  {/* Overlay dengan action buttons */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="bg-white/90 hover:bg-white"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="bg-white/90 hover:bg-white"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Categories badges */}
                  {template.categories && (
                    <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                      {template.categories.map((c, index) => (
                        <Badge
                          key={index}
                          className={`${getCategoryColorClass(c)}`}
                        >
                          {c}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </CardHeader>

              <CardContent className="p-2 sm:p-4 pt-0 space-y-3 flex flex-col h-full justify-between">
                <div>
                  <CardTitle className="text-base sm:text-lg group-hover:text-primary transition-colors line-clamp-1">
                    {template.title}
                  </CardTitle>
                  <p className="text-sm sm:text-lg font-bold">
                    {formatRupiah(template.price)}
                  </p>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 flex-1">
                  {template.description}
                </p>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() =>
                      router.push(
                        "https://payreless.com/product/detail/module-whatsapp-gateway",
                      )
                    }
                  >
                    Beli Sekarang
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
